import {ENQUEUE_RESTORE_VERSION} from '@packages/systems/designer/actions';
import {tokens} from '@packages/systems/spring';

export const outOfSyncBranchActions = [ENQUEUE_RESTORE_VERSION] as const;

export const branchStage = 'branchStaging';

export const branchIconColor = tokens.colors.orangeText;

export const BRANCH_MODES = {
  // actions that can happen on main
  MAIN: 'main',
  // actions that can happen on a branch
  BRANCH: 'branch',
} as const;

export type BranchModeType = (typeof BRANCH_MODES)[keyof typeof BRANCH_MODES];

/** Polling intervals for branch update and merge background tasks */
export const FETCH_BRANCH_TASK_MAX_TRIES = 100;

/**
 * The polling intervals are designed to increase exponentially to avoid overwhelming the queue,
 * with a maximum interval of 5000ms to stay within the 3-minute queue timeout window.
 */
export const FETCH_BRANCH_TASK_POLLING_INTERVAL = [
  {from: 1, to: 20, interval: 250}, // first 20 tries every 250ms (5s)
  {from: 20, to: 40, interval: 500}, // sequent 20 tries every 500ms (10s)
  {from: 40, to: 60, interval: 1000}, // sequent 20 tries every 1s (20s)
  {from: 60, to: 75, interval: 2000}, // sequent 15 tries every 2s (30s)
  {from: 75, to: 85, interval: 3000}, // sequent 10 tries every 3s (30s)
  {from: 85, to: 95, interval: 4000}, // sequent 10 tries every 4s (40s)
  {from: 95, to: FETCH_BRANCH_TASK_MAX_TRIES, interval: 5000}, // final 5 tries every 5s (25s)
];

/**
 * Two constants are needed because AppError only takes the message (it adds the prefix automatically),
 * but the reducer checks the full error string. Deriving _ERROR from _MESSAGE ensures
 * the message is defined once and stays in sync.
 */
export const BRANCH_LIBRARY_INSTALL_IN_PROGRESS_MESSAGE =
  'A library installation is currently in progress. Please wait for it to complete.';

export const BRANCH_LIBRARY_INSTALL_IN_PROGRESS_ERROR = `Conflict with server data: ${BRANCH_LIBRARY_INSTALL_IN_PROGRESS_MESSAGE}`;

export const BRANCH_PUBLISH_IN_PROGRESS_MESSAGE =
  'A site publish is in progress. Please try again after publishing is complete.';

export const BRANCH_PUBLISH_IN_PROGRESS_ERROR = `Conflict with server data: ${BRANCH_PUBLISH_IN_PROGRESS_MESSAGE}`;

// Matched verbatim against `appErrors.BranchSyncPending.message`; keep the two
// in sync or branch merge/update crashes the Designer again.
export const BRANCH_SYNC_PENDING_MESSAGE =
  'Recent edits are still syncing and conflicts cannot be verified yet. Please try again in a moment.';

// A branch request answered by a gateway error page (e.g. a 504 timeout)
// instead of the app's JSON error.
export const BRANCH_GATEWAY_ERROR = 'BranchGatewayError';

export const BRANCH_MERGE_UPDATE_ALLOWED_ERRORS = new Set<string>([
  'ValidationErrorError',
  'BranchUnresolvedConflictsError',
  'BranchInvalidUserComponentError',
  'LibraryChecksError',
  // NestJS ConflictException name — covers all 409s from DDA branch
  // endpoints (merge blocked by publish, merge blocked by another merge,
  // lifecycle gate rejection). These are always safe guard rejections.
  'ConflictException',
  'Conflict with server data: Another branch merge is in progress. Please try again later.',
  'Unresolved conflicts, unable to merge branch',
  // Thrown when a branch's dataVersion is behind the site's after an
  // in-flight site data migration. The user can recover by refreshing the
  // browser so version numbers refetch and the migration completes.
  'Site data version mismatch: Branch data version does not match site data version.',
  BRANCH_LIBRARY_INSTALL_IN_PROGRESS_ERROR,
  BRANCH_PUBLISH_IN_PROGRESS_ERROR,
  // Multiplayer sync webhook failures — safe to surface without crashing designer
  'MultiplayerSyncFailedError',
  BRANCH_SYNC_PENDING_MESSAGE,
  BRANCH_GATEWAY_ERROR,
]);
